/**
 * Extrae datos de un XML o PDF de factura para prellenar (nunca crear
 * automáticamente) una identidad fiscal.
 *
 * XML: prioriza siempre los nodos estructurados del comprobante electrónico
 * de Hacienda (Costa Rica) — el `Receptor` es el doctor/pagador al que Clean
 * Blue le factura.
 *
 * PDF: no hay parser de PDF estructurado disponible (ni en el navegador ni
 * en el Worker), así que es heurística sobre el texto extraído — asistencia
 * de prellenado, nunca fuente de verdad. Solo funciona si el PDF tiene capa
 * de texto (no es un escaneo).
 */

import type { TaxIdType } from '@cleanblue/shared';

export interface BillingPrefill {
  legalName?: string;
  taxIdType?: TaxIdType;
  taxId?: string;
  billingEmail?: string;
  phone?: string;
  address?: string;
}

/** Cuenta cuántos campos reconocibles por el formulario se pudieron llenar. */
export function prefillFieldCount(p: BillingPrefill): number {
  return [p.legalName, p.taxId, p.billingEmail, p.phone, p.address].filter((v) => !!v).length;
}

/**
 * La identidad propia de Clean Blue — el emisor de toda factura que se va a
 * importar. Un import nunca puede terminar prellenando el formulario de un
 * doctor con estos datos: si el XML no trae `Receptor` y el código cae a
 * `null` está bien, pero la heurística de PDF no tiene esa garantía
 * estructural, así que se filtra explícito acá, después de extraer.
 */
const CLEAN_BLUE_TAX_ID = '3102936637';
const CLEAN_BLUE_NAME_RE = /clean\s*blue/i;

/** Descarta cualquier campo que en realidad sea el dato del emisor (Clean Blue), no del receptor. */
function stripOwnIdentity(p: BillingPrefill): BillingPrefill {
  const result = { ...p };
  if (result.taxId?.replace(/\D/g, '') === CLEAN_BLUE_TAX_ID) {
    delete result.taxId;
    delete result.taxIdType;
  }
  if (result.legalName && CLEAN_BLUE_NAME_RE.test(result.legalName)) delete result.legalName;
  if (result.billingEmail?.toLowerCase() === CLEAN_BLUE_EMAIL) delete result.billingEmail;
  return result;
}

const HACIENDA_TAX_ID_TYPE: Record<string, TaxIdType> = {
  '01': 'FISICA',
  '02': 'JURIDICA',
  '03': 'DIMEX',
  '04': 'NITE',
};

function text(root: Element | Document, tag: string): string | undefined {
  const el = root.getElementsByTagName(tag)[0];
  const value = el?.textContent?.trim();
  return value ? value : undefined;
}

/**
 * Lee el XML de una factura electrónica costarricense (Hacienda v4.3) y
 * extrae los datos del `Receptor` — quien recibe la factura, no quien la
 * emite. Acepta cualquier tipo de comprobante (Factura, Tiquete, Nota) porque
 * todos comparten esa estructura.
 */
export function parseFiscalXml(xmlText: string): BillingPrefill | null {
  const doc = new DOMParser().parseFromString(xmlText, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length > 0) return null;

  const receptor = doc.getElementsByTagName('Receptor')[0];
  if (!receptor) return null;

  const legalName = text(receptor, 'Nombre') ?? text(receptor, 'NombreComercial');
  const identificacion = receptor.getElementsByTagName('Identificacion')[0];
  const tipoCodigo = identificacion ? text(identificacion, 'Tipo') : undefined;
  const taxId = identificacion ? text(identificacion, 'Numero') : undefined;
  const taxIdType = tipoCodigo ? HACIENDA_TAX_ID_TYPE[tipoCodigo] : undefined;
  const billingEmail = text(receptor, 'CorreoElectronico');

  const telefono = receptor.getElementsByTagName('Telefono')[0];
  const phone = telefono ? text(telefono, 'NumTelefono') : undefined;

  const ubicacion = receptor.getElementsByTagName('Ubicacion')[0];
  const address = ubicacion ? text(ubicacion, 'OtrasSenas') : undefined;

  const result = stripOwnIdentity({ legalName, taxIdType, taxId, billingEmail, phone, address });
  return prefillFieldCount(result) > 0 ? result : null;
}

const CLEAN_BLUE_EMAIL = 'algalucr@gmail.com';
const EMAIL_RE = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi;
// Cédula física 9-10 dígitos, jurídica 10 dígitos, DIMEX 11-12: cualquier corrida de 9 a 12
// dígitos (con guiones opcionales) cerca de la palabra clave, para no adivinar cualquier número.
const TAX_ID_NEAR_RE = /(?:c[ée]dula|identificaci[óo]n|dimex|nite)[^\d]{0,20}([\d-]{9,15})/i;
const PHONE_RE = /(?:tel[ée]fono|tel\.?|whatsapp)[^\d]{0,10}(\d{4}[-\s]?\d{4})/i;
const ADDRESS_RE = /(?:direcci[óo]n|domicilio)[:\s]+([^\n\r]{5,150})/i;
const NAME_RE = /(?:raz[óo]n social|nombre(?: del cliente| del receptor)?|cliente)[:\s]+([^\n\r]{2,120})/i;

/**
 * Heurística sobre el texto de un PDF. Solo llena lo que encuentra con
 * confianza razonable (una etiqueta reconocible al lado); nunca adivina.
 */
export function parseFiscalPdfText(pdfText: string): BillingPrefill | null {
  const flat = pdfText.replace(/\r/g, '');

  const legalName = flat.match(NAME_RE)?.[1]?.trim();
  const taxIdRaw = flat.match(TAX_ID_NEAR_RE)?.[1];
  const taxId = taxIdRaw?.replace(/[^\d-]/g, '');
  // El encabezado de Clean Blue (emisor) siempre trae su propio correo antes
  // que cualquier dato del receptor — tomar "el primer correo del texto" a
  // ciegas es tomar el nuestro. Se descarta explícitamente, no solo el primero.
  const billingEmail = [...flat.matchAll(EMAIL_RE)]
    .map((m) => m[0])
    .find((email) => email.toLowerCase() !== CLEAN_BLUE_EMAIL);
  const phone = flat.match(PHONE_RE)?.[1]?.replace(/\s/g, '-');
  const address = flat.match(ADDRESS_RE)?.[1]?.trim();

  const result = stripOwnIdentity({ legalName, taxId, billingEmail, phone, address });
  return prefillFieldCount(result) > 0 ? result : null;
}

/** Extrae el texto de todas las páginas de un PDF vía `pdfjs-dist`, corriendo en el navegador. */
export async function extractPdfText(file: File): Promise<string> {
  const pdfjsLib = await import('pdfjs-dist');
  const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
  pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

  const buffer = await file.arrayBuffer();
  const doc = await pdfjsLib.getDocument({ data: buffer }).promise;
  const pages: string[] = [];
  for (let i = 1; i <= doc.numPages; i += 1) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    pages.push(content.items.map((it) => ('str' in it ? it.str : '')).join(' '));
  }
  return pages.join('\n');
}
